import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Field, NumberField, DateField, SelectRow, INPUT_CLS } from "./trade/FormControls";
import DerivativeFields, { DERIVATIVES, derivedQuantity } from "./trade/DerivativeFields";
import LtpBadge, { useLiveLtp } from "./trade/LtpBadge";
import PreCheckPanel from "./trade/PreCheckPanel";
import EmotionSelector from "./journal/EmotionSelector";

const EXCHANGES = ["NS", "BO"];
const toNum = (v) => (v === "" || v == null ? null : Number(v));
const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  broker: "SHOONYA",
  category: "DELIVERY",
  stock: "",
  exchange: "NS",
  transaction: "BOUGHT",
  rate: "",
  quantity: "",
  lots: "",
  lot_size: "",
  stop_loss: "",
  target: "",
  date_of_purchase: "",
  status: "HOLDING",
  exit_rate: "",
  exit_date: "",
  reason: "",
  notes: "",
  emotion: "",
};

// Controlled inputs need "" not null; keep ids/metadata from the saved trade intact
function toFormState(initial) {
  const form = { ...empty, date_of_purchase: today() };
  if (!initial) return form;
  Object.entries(initial).forEach(([k, v]) => { form[k] = v == null && k in empty ? "" : v; });
  return form;
}

function buildPayload(form, isDerivative, effectiveQty) {
  return {
    ...form,
    rate: Number(form.rate),
    quantity: effectiveQty ?? 0,
    lots: isDerivative ? toNum(form.lots) : null,
    lot_size: isDerivative ? toNum(form.lot_size) : null,
    stop_loss: toNum(form.stop_loss),
    target: toNum(form.target),
    exit_rate: toNum(form.exit_rate),
    exit_date: form.exit_date || null,
    date_of_purchase: form.date_of_purchase || null,
    emotion: form.emotion || null,
    notes: form.notes || null,
  };
}

export default function TradeDialog({ open, onClose, onSaved, initial, reference }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [precheck, setPrecheck] = useState(null);
  const [prechecking, setPrechecking] = useState(false);
  const [lotPresets, setLotPresets] = useState(null);
  const [lotHint, setLotHint] = useState("");
  const { ltp, loading: ltpLoading } = useLiveLtp(open, form.stock, form.exchange);

  const isDerivative = DERIVATIVES.includes(form.category);
  const effectiveQty = isDerivative ? derivedQuantity(form) : toNum(form.quantity);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setPrecheck(null);
    setLotHint("");
    setForm(toFormState(initial));
    api.get("/lot-sizes").then((r) => setLotPresets(r.data)).catch(() => setLotPresets(null));
  }, [open, initial]);

  // Auto-fill Lot Size from presets / past trades when symbol changes on a derivative trade
  useEffect(() => {
    if (!open || !lotPresets || !DERIVATIVES.includes(form.category)) return;
    const sym = (form.stock || "").trim().toUpperCase();
    const size = lotPresets.sizes?.[sym];
    if (!size) { if (lotHint) setLotHint(""); return; }
    if (form.lot_size !== "" && lotHint === "") return; // user typed a value — keep it
    if (String(form.lot_size) !== String(size)) setForm((f) => ({ ...f, lot_size: String(size) }));
    setLotHint(lotPresets.source?.[sym] === "history" ? `from your last ${sym} trade` : `${sym} preset`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lotPresets, form.stock, form.category]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (precheck) setPrecheck(null); };

  async function runPrecheck() {
    setPrechecking(true);
    setPrecheck(null);
    try {
      const { data } = await api.post("/trades/precheck", buildPayload(form, isDerivative, effectiveQty));
      setPrecheck(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Pre-check failed");
    } finally {
      setPrechecking(false);
    }
  }

  async function save() {
    setBusy(true);
    setErr("");
    try {
      const payload = buildPayload(form, isDerivative, effectiveQty);
      if (isEdit) await api.put(`/trades/${initial.id}`, payload);
      else await api.post("/trades", payload);
      toast.success(isEdit ? "Trade updated" : "Trade added");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!reference) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="trade-dialog" className="bg-[#121318] border-[#27272A] text-white max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xs tracking-[0.25em] uppercase text-[#A1A1AA]">{isEdit ? "Edit Trade" : "New Trade"}</DialogTitle>
          <DialogDescription className="sr-only">Enter the trade details and save.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Broker">
            <SelectRow value={form.broker} onChange={(v) => set("broker", v)} options={reference.brokers} testid="trade-broker" />
          </Field>
          <Field label="Category">
            <SelectRow value={form.category} onChange={(v) => set("category", v)} options={reference.categories} testid="trade-category" />
          </Field>
          <Field label="Stock Symbol">
            <div className="relative">
              <Input data-testid="trade-stock" value={form.stock} onChange={(e) => set("stock", e.target.value.toUpperCase())}
                placeholder="RELIANCE" className={`${INPUT_CLS} uppercase pr-24`} />
              {form.stock.length >= 2 && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1" data-testid="ltp-inline">
                  <LtpBadge ltp={ltp} loading={ltpLoading} onUse={(v) => set("rate", v)} />
                </div>
              )}
            </div>
          </Field>
          <Field label="Exchange">
            <SelectRow value={form.exchange} onChange={(v) => set("exchange", v)} options={EXCHANGES} testid="trade-exchange" />
          </Field>
          <Field label="Transaction">
            <SelectRow value={form.transaction} onChange={(v) => set("transaction", v)} options={reference.transactions} testid="trade-transaction" />
          </Field>
          <DateField label="Date" testid="trade-date" value={form.date_of_purchase} onChange={(v) => set("date_of_purchase", v)} />
          <NumberField label="Rate" testid="trade-rate" value={form.rate} onChange={(v) => set("rate", v)} />
          {isDerivative ? (
            <DerivativeFields form={form} set={set} lotHint={lotHint} onLotSizeInput={(v) => { setLotHint(""); set("lot_size", v); }} />
          ) : (
            <NumberField label="Quantity" testid="trade-quantity" step="1" value={form.quantity} onChange={(v) => set("quantity", v)} />
          )}
          <NumberField label="Stop Loss" testid="trade-stoploss" value={form.stop_loss} onChange={(v) => set("stop_loss", v)} />
          <NumberField label="Target" testid="trade-target" value={form.target} onChange={(v) => set("target", v)} />
          <Field label="Status">
            <SelectRow value={form.status} onChange={(v) => set("status", v)} options={reference.statuses} testid="trade-status" />
          </Field>
          {form.status === "CLOSED" && (
            <>
              <NumberField label="Exit Rate" testid="trade-exitrate" value={form.exit_rate} onChange={(v) => set("exit_rate", v)} />
              <DateField label="Exit Date" testid="trade-exitdate" value={form.exit_date} onChange={(v) => set("exit_date", v)} />
            </>
          )}
          <div className="col-span-2">
            <Field label="Emotion (optional — helps AI coach)">
              <EmotionSelector value={form.emotion} onChange={(v) => set("emotion", v)} testidPrefix="trade-emotion" rowTestid="emotion-row-dialog" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Reason / Notes">
              <Textarea data-testid="trade-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                className={INPUT_CLS} placeholder="Why this trade…" />
            </Field>
          </div>
        </div>

        {!isEdit && (
          <PreCheckPanel precheck={precheck} prechecking={prechecking} onRun={runPrecheck} canRun={!!form.stock && !!form.rate && !!effectiveQty} />
        )}

        {err && <div data-testid="trade-error" className="text-xs text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 rounded-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <Button data-testid="trade-cancel" variant="outline" onClick={onClose}
            className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">Cancel</Button>
          <Button data-testid="trade-save" onClick={save} disabled={busy} className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
            {busy ? "Saving…" : isEdit ? "Update Trade" : "Add Trade"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
